import { images } from "src/images";
import {
  IBase,
  IClient,
  IMessage,
  INotificationEventOption,
  IRoute,
  IServiceInfo,
  ITown,
  IUserRegular,
} from "../models";
import { formData } from "./messageData";
import { LiteralService, RouteService, StorageService, TripsService, UsersService, UtilsService } from "../services";
import moment from "moment";
import {
  CommunicationUserType,
  InputType,
  NotificationEvent,
  UserRole,
  UserSortType,
  UserStatus,
  UserType,
} from "../models/enums";
import { MessageReceiver } from "../models/enums/messageReceiver";
import { MessageChannel } from "../models/enums/messageChannel";
import { ServiceInfoService } from "../services/service-info/service-info.service";
import { TooltipPosition } from "../shared/tooltip/tooltip-position";
import { getExpeditionOptions, tripSummaryOption } from "./expeditionOptions";

export class ReceiverMessageData {
  showMore: boolean;
  search?: boolean;
  value?: string;

  constructor(data: Partial<ReceiverMessageData>) {
    Object.assign(this, data);
  }
}

export async function getMessageForm(message: IMessage, literalService: LiteralService, storageService: StorageService) {
  if (message) {
    const data: formData[] = [
      {
        id: `client-${message.id}`,
        img: images.messages.clients,
        title: 'messages.data.client',
        value: message.client.name,
        hidden: clients.length === 1
      },
      {
        id: `from-${message.id}`,
        img: images.messages.from,
        title: 'messages.data.from',
        value: getMessageSenderLabel(message, literalService)
      },
      {
        id: `receiver-${message.id}`,
        img: images.messages.receiver,
        title: 'messages.data.receiver',
        value: [literalService.get('messages.receiver.' + message.receiver!.toLowerCase(), true), message.receiverName]
          .filter(Boolean)
          .join(' - ')
      },
      {
        id: `channel-${message.id}`,
        img: images.messages.channel.multiple,
        title: 'messages.data.messageChannel',
        value: message.channels!.map((channel) => literalService.get(`messages.channel.${channel.toLowerCase()}`, true)).join(', ')
      },
            {
        id: `subject-${message.id}`,
        img: images.messages.affair,
        title: 'messages.data.subject',
        value: message.subject
      },
      {
        id: `description-${message.id}`,
        img: images.messages.description,
        title: 'messages.data.description',
        value: message.content,
        tooltipPosition: TooltipPosition.LEFT,
        edit: {
          required: true,
          editable: false,
          inputType: InputType.LONG_TEXT,
          valueToChange: 'description'
        }
      },
      {
        id: `date-${message.id}`,
        img: images.messages.date,
        title: 'messages.data.dateSent',
        value: moment(message.dateTime).format('DD/MM/yyyy')
      },
    ];
    return data;
  }
  return [];
}

export function getReceiverTypeOptions(
  literalService: LiteralService,
  communicationUserType: CommunicationUserType | undefined,
  adminRole: UserRole,
) {
  const canUseTownAndServiceReceivers = adminRole === UserRole.SUPERADMIN || adminRole === UserRole.ADMIN;
  return Object.values(MessageReceiver)
    .filter((receiver) => {
      if (receiver === MessageReceiver.ADMINS) return false;
      if (communicationUserType === CommunicationUserType.USERS && receiver === MessageReceiver.DRIVER) return false;
      if (
        communicationUserType === CommunicationUserType.DRIVERS &&
        (receiver === MessageReceiver.USER || receiver === MessageReceiver.TOWN || receiver === MessageReceiver.SERVICE)
      )
        return false;
      return canUseTownAndServiceReceivers || (receiver !== MessageReceiver.TOWN && receiver !== MessageReceiver.SERVICE);
    })
    .map((receiver) => ({
      id: receiver,
      value: receiver,
      label: literalService.get(`messages.receiver.${receiver.toLowerCase()}`, true),
    }));
}

export function getMessageSenderLabel(message: IMessage, literalService: LiteralService): string {
  if (!message.systemSender) return message.sender;
  const systemSenderName = literalService.get('messages.systemSender');
  if (!message.event) return systemSenderName;
  return `${systemSenderName} - ${literalService.get(`notificationSettings.events.${message.event}`, true)}`;
}

export function getNotificationEventOptions(literalService: LiteralService): INotificationEventOption[] {
  return Object.values(NotificationEvent)
    .map((event) => ({ id: event, name: literalService.get(`notificationSettings.events.${event}`, true) }))
    .sort((first, second) => first.name.localeCompare(second.name));
}

let pageSearched = 1;
let receiversSearched: any = [];

let page = 1;
let receivers: any = [];

let servicesPage = 1;
let services: any[] = [];

let routesPage = 1;
let routes: any[] = [];

let clients: any[] = [];

export async function getNewMessageForm(
  message: IMessage,
  literalService: LiteralService,
  usersService: UsersService,
  storageService: StorageService,
  utilsService: UtilsService,
  serviceInfoService: ServiceInfoService,
  routeService: RouteService,
  tripsService: TripsService,
  currentStep: number,
  communicationUserType: CommunicationUserType,
  receiverData?: ReceiverMessageData,
  changes?: any[]
) {
  if (!message.client) {
    await getClientsTowns(usersService).then((res) => {
      clients = res.clients;
      if (res.clients.length === 1) message.client = res.clients[0].value;
    });
  }

  const adminRole = await storageService.getRole();
  const hideClientsStep = clients.length === 1;
  const lastStep = hideClientsStep ? 2 : 3;

  const receiverTypes = getReceiverTypeOptions(literalService, communicationUserType, adminRole);
  let channels: any[] = [];

  Object.values(MessageChannel).forEach((channel: MessageChannel) => {
    channels.push({
      id: channel,
      name: literalService.get(`messages.channel.${channel.toLowerCase()}`, true)
    })
  });

  if (changes && changes.includes('receiver')) {
      receivers = [];
      page = 1;
      receiversSearched = [];
      pageSearched = 1;
      message.receiverIds = [];
      services = [];
      servicesPage = 1;
      routes = [];
      routesPage = 1;
  }

  if (receiverData && receiverData.search) {
    receivers = [];
    page = 1;
    if (receiverData.showMore) {
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverServiceId')) servicesPage += 1;
      else if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverRouteId')) routesPage += 1;
      else pageSearched += 1;
    } else {
      receiversSearched = [];
      pageSearched = 1;
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverServiceId')) {
        services = [];
        servicesPage = 1;
      }
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverRouteId')) {
        routes = [];
        routesPage = 1;
      }
    }
    switch(message.receiver) {
      case MessageReceiver.USER:
        const moreUsers = await searchUsers(
          usersService,
          utilsService,
          UserType.Regular,
          message.client?.id,
          receiverData.value,
          message.receiverIds
        );
        receiversSearched = [
          ...receiversSearched,
          ...moreUsers
        ];
        break;
      case MessageReceiver.TOWN:
        receivers = await getTownsByClient(usersService, message.client.id, message.receiverIds);
        break;
      case MessageReceiver.SERVICE:
        const newSearchedPage = await getServiceInfos(serviceInfoService, message.client.id!, receiverData.value, undefined, message.receiverIds);
        receiversSearched = [
          ...receiversSearched,
          ...newSearchedPage
        ];
        break;
      case MessageReceiver.EXPEDITION:
        if (changes && changes.includes('receiverIds')) {
          const newSearchedPage = await getExpeditionsByRouteId(tripsService, message.receiverRouteId!.id, [], pageSearched, 10, receiverData.value);
          receiversSearched = [
            ...receiversSearched,
            ...newSearchedPage
          ];
        } else if (changes && changes.includes('receiverRouteId')) {
          const moreRoutes = await getRoutesByServiceInfo(routeService, message.receiverServiceId!.id, receiverData.value || '');
          routes = [
            ...routes,
            ...moreRoutes
          ];
          routes = addSelectedValues(utilsService, routes, [message.receiverRouteId]);
        } else if (changes && changes.includes('receiverServiceId')) {
          const moreServices = await getServiceInfos(serviceInfoService, message.client.id!, receiverData.value, undefined, [message.receiverServiceId]);
          services = [
            ...services,
            ...moreServices
          ];
          services = addSelectedValues(utilsService, services, [message.receiverServiceId]);
        }
        break;
      case MessageReceiver.DRIVER:
        const moreDrivers = await searchUsers(
          usersService,
          utilsService,
          UserType.Driver,
          message.client?.id,
          receiverData.value,
          message.receiverIds
        );
        receiversSearched = [
          ...receiversSearched,
          ...moreDrivers
        ];
        break;
    }
    receiversSearched = addSelectedValues(utilsService, receiversSearched, message.receiverIds);
  } else {
    receiversSearched = [];
    pageSearched = 1;
    if (receiverData?.showMore) {
      page += 1;
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverServiceId')) servicesPage += 1;
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverRouteId')) routesPage += 1;
    } 
    else {
      page = 1;
      receivers = [];
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverServiceId')) {
        if (!receiverData) {
          message.receiverRouteId = undefined;
          routes = [];
          routesPage = 1;
        } else {
          services = [];
          servicesPage = 1;
        }
      }
      if (message.receiver === MessageReceiver.EXPEDITION && changes && changes.includes('receiverRouteId') && receiverData) {
        routes = [];
        routesPage = 1;
      }
    }
    switch(message.receiver) {
      case MessageReceiver.USER:
        const moreUsers = await getUsers(
          usersService,
          utilsService,
          UserType.Regular,
          message.client?.id,
          message.receiverIds
        );
        receivers = [
          ...receivers,
          ...moreUsers
        ];
        break;
      case MessageReceiver.TOWN:
        receivers = await getTownsByClient(usersService, message.client.id, message.receiverIds);
        break;
      case MessageReceiver.SERVICE:
        const moreServices = await getServiceInfos(serviceInfoService, message.client.id!, undefined, undefined, message.receiverIds);
        receivers = [
          ...receivers,
          ...moreServices
        ];
        break;
      case MessageReceiver.EXPEDITION:
        if (receiverData) {
          if (changes && changes.includes('receiverIds')) {
            const newSearchedPage = await getExpeditionsByRouteId(tripsService, message.receiverRouteId!.id, [], page, 10);
            receivers = [
              ...receivers,
              ...newSearchedPage
            ];
          } else if (changes && changes.includes('receiverRouteId')) {
            const moreRoutes = await getRoutesByServiceInfo(routeService, message.receiverServiceId!.id, receiverData?.value || '');
            routes = [
              ...routes,
              ...moreRoutes
            ];
          } else if (changes && changes.includes('receiverServiceId')) {
            const moreServices = await getServiceInfos(serviceInfoService, message.client.id!);
            services = [
              ...services,
              ...moreServices
            ];
          }
        } else {
          if (message.receiverRouteId) {
            receivers = await getExpeditionsByRouteId(tripsService, message.receiverRouteId!.id, [], page, 10);
          } else if (changes && changes.includes('receiverServiceId')) {
            routes = await getRoutesByServiceInfo(routeService, message.receiverServiceId!.id, '');
          } else {
            services = await getServiceInfos(serviceInfoService, message.client.id!);
          }
        }
        break;
      case MessageReceiver.DRIVER:
        const moreDrivers = await getUsers(
          usersService,
          utilsService,
          UserType.Driver,
          message.client?.id,
          message.receiverIds
        );
        receivers = [
          ...receivers,
          ...moreDrivers
        ];
        break;
    }
    receivers = addSelectedValues(utilsService, receivers, message.receiverIds);
  }

  const data: formData[] = [
    {
      id: 'client',
      img: images.messages.clients,
      title: 'messages.data.client',
      value: currentStep === 1 ? message.client : message.client?.name,
      color: true,
      step: 1,
      hidden: hideClientsStep,
      edit: {
        editable: currentStep! === 1 ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: clients,
        valueToChange: 'client',
        placeholder: literalService.get('messages.data.client', true),
      }
    },
    {
      id: 'receiver',
      img: images.messages.receiver,
      title: 'messages.data.receiver',
      value: currentStep! === (lastStep - 1) ? message.receiver : literalService.get(`messages.receiver.${message.receiver?.toLowerCase()}`, true),
      color: true,
      step: (lastStep - 1),
      hidden: currentStep < (lastStep - 1),
      edit: {
        editable: currentStep! === (lastStep - 1) ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: receiverTypes,
        valueToChange: 'receiver',
      }
    },
    {
      id: 'toUser',
      img: images.messages.user,
      title: `messages.receiver.user`,
      value: message.receiverIds,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep || (message.receiver !== MessageReceiver.USER && message.receiver !== MessageReceiver.DRIVER),
      edit: {
        editable: true,
        required: message.receiver === MessageReceiver.USER ? true : false,
        inputType: InputType.MULTISELECT_SCROLL,
        values: receivers.length > 0 ? receivers : receiversSearched,
        valueToChange: 'receiverIds',
        placeholder: literalService.get('messages.receiver.user', true),
        customSearchEnabled: true,
      }
    },
    {
      id: 'toTown',
      img: images.messages.town,
      title: `messages.receiver.town`,
      value: message.receiverIds,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep || message.receiver !== MessageReceiver.TOWN,
      edit: {
        editable: true,
        required: message.receiver === MessageReceiver.TOWN ? true : false,
        inputType: InputType.MULTISELECT_SCROLL,
        values: receivers,
        valueToChange: 'receiverIds',
        placeholder: literalService.get('messages.receiver.town', true),
        customSearchEnabled: false,
      }
    },
    {
      id: 'toExpeditionService',
      img: images.messages.service,
      title: `messages.receiver.service`,
      value: message.receiverServiceId,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep || message.receiver !== MessageReceiver.EXPEDITION,
      edit: {
        editable: true,
        required: message.receiver === MessageReceiver.EXPEDITION ? true : false,
        inputType: InputType.INFINITE_SCROLL,
        values: services,
        valueToChange: 'receiverServiceId',
        placeholder: literalService.get('messages.receiver.service', true),
        customSearchEnabled: true,
      }
    },
    {
      id: 'toExpeditionRoute',
      img: images.messages.route,
      title: `messages.receiver.routes`,
      value: message.receiverRouteId,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep || message.receiver !== MessageReceiver.EXPEDITION,
      edit: {
        editable: true,
        required: message.receiver === MessageReceiver.EXPEDITION ? true : false,
        inputType: InputType.INFINITE_SCROLL,
        values: routes,
        valueToChange: 'receiverRouteId',
        placeholder: literalService.get('messages.receiver.routes', true),
        customSearchEnabled: true,
      }
    },
    {
      id: 'toExpedition',
      img: images.messages.expedition,
      title: `messages.receiver.expedition`,
      value: message.receiverIds,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep || message.receiver !== MessageReceiver.EXPEDITION,
      edit: {
        editable: true,
        required: message.receiver === MessageReceiver.TOWN ? true : false,
        inputType: InputType.MULTISELECT_SCROLL,
        values: receivers.length > 0 ? receivers : receiversSearched,
        valueToChange: 'receiverIds',
        placeholder: literalService.get('messages.receiver.expedition', true),
        customSearchEnabled: true,
      }
    },
    {
      id: 'toService',
      img: images.messages.service,
      title: `messages.receiver.service`,
      value: message.receiverIds,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep || message.receiver !== MessageReceiver.SERVICE,
      edit: {
        editable: true,
        required: message.receiver === MessageReceiver.SERVICE ? true : false,
        inputType: InputType.MULTISELECT_SCROLL,
        values: receivers.length > 0 ? receivers : receiversSearched,
        valueToChange: 'receiverIds',
        placeholder: literalService.get('messages.receiver.service', true),
        customSearchEnabled: true,
      }
    },
    {
      id: 'channels',
      img: images.messages.channel.multiple,
      title: `messages.data.messageChannel`,
      value: message.channels?.join(','),
      dataToShow: 'name',
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep,
      placeholder: literalService.get('messages.data.messageChannel', true),
      edit: {
        editable: true,
        required: true,
        inputType: InputType.MULTISELECT,
        values: channels,
        selectedValues: message.channels,
        valueToChange: 'channels',
      }
    },
    {
      id: 'affair',
      img: images.messages.affair,
      title: `messages.data.subjectOptional`,
      value: message.subject,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep,
      edit: {
        editable: true,
        required: true,
        inputType: InputType.TEXT,
        values: receivers,
        valueToChange: 'subject',
        placeholder: literalService.get('messages.data.subject', true),
      }
    },
    {
      id: 'description',
      img: images.messages.description,
      title: `messages.data.description`,
      value: message.content,
      color: true,
      step: lastStep,
      hidden: currentStep !== lastStep,
      edit: {
        editable: true,
        required: true,
        inputType: InputType.LONG_TEXT,
        valueToChange: 'content',
        placeholder: literalService.get('maxChar250', true),
        maxLength: 250
      }
    },
  ];
  return data
}

export async function getClientsTowns(usersService: UsersService) {
  return await usersService.getPossibleClientTowns().then((res) => {
    const clients: any[] = [];
    const towns: any[] = [];
    res.clients.map((client: IClient) => {
      clients.push({
        id: client.id,
        value: client,
        label: client.name,
        name: client.name,
      });
    });
    res.towns.map((town: ITown) => {
      towns.push({
        id: town.id,
        value: town,
        label: town.name,
        name: town.name,
      });
    });

    return { clients, towns };
  });
}

export async function getUsers(
  usersService: UsersService,
  utilsService: UtilsService,
  userType: UserType,
  clientId?: number,
  userSelected?: any[],
  specificPage?: number
) {
  return await usersService
    .filterByUserType(
      userType,
      UserSortType.ALPHABETICALLY,
      specificPage ? specificPage : page,
      10,
      clientId ? [clientId] : [],
      [],
      [],
      [],
      undefined,
    )
    .then((res) => {
      let list: any[] = [];
      res.content.map((user: IUserRegular) => {
        const exists = userSelected?.some((selected) => selected.id === user.id);
        if (
          !exists &&
          (user.status?.toUpperCase() === UserStatus.ACTIVE ||
          user.status?.toUpperCase() === UserStatus.BLOCKED)
        ) {
          const label = utilsService.getUserLabel(user);
          list.push({
            id: user.id,
            value: utilsService.sanitizeObject(user),
            label,
          });
        }
      });
      return list;
    });
}

export async function searchUsers(
  usersService: UsersService,
  utilsService: UtilsService,
  userType: UserType,
  clientId?: number,
  searchValue?: string,
  userSelected?: any[],
  specificPage?: number
) {
  return await usersService
    .filterByUserType(
      userType,
      UserSortType.ALPHABETICALLY,
      specificPage ? specificPage : pageSearched,
      10,
      clientId ? [clientId] : [],
      [],
      [],
      [],
      searchValue,
    )
    .then((res) => {
      let list: any[] = [];
      res.content.map((user: IUserRegular) => {
        const exists = userSelected?.some((selected) => selected.id === user.id);
        if (
          !exists &&
          (user.status?.toUpperCase() === UserStatus.ACTIVE ||
          user.status?.toUpperCase() === UserStatus.BLOCKED)
        ) {
          const label = utilsService.getUserLabel(user);
          list.push({
            id: user.id,
            value: utilsService.sanitizeObject(user),
            label,
          });
        }
      });
      return list;
    });
}

export function addSelectedValues(utilsService: UtilsService, list: any[], selectedValues: any) {
  const newList = list;
  selectedValues && selectedValues.map((value: any) => {
    if (!value) return;
    const exists = newList.find((ul: any) => ul.id === value.id);
    if (!exists) {
      newList.unshift({
        id: value.id,
        value: utilsService.sanitizeObject(value),
        label: (value.licenseNumber || value.dni || value.phone || value.email) ? utilsService.getUserLabel(value) : value.tag || value.name,
      });
    }
  });
  return newList;
}

export async function getTownsByClient(usersService: UsersService, clientId: any, selected?: any[]) {
  return await usersService.getTownsByClient(clientId).then((res: any) => {
    const towns: any[] = [];
    res.map((town: ITown) => {
      const exists = selected?.some((it) => it.id === town.id);
      if (!exists) {
        towns.push({ id: town.id, value: town, label: town.name });
      }
    });
    return towns;
  });
}

export async function getServiceInfos(serviceInfoService: ServiceInfoService, clientId?: number, searchValue?: string, specificPage?: number, selected?: any[]) {
  return await serviceInfoService.filterServiceInfo(specificPage ? specificPage : page, 10, clientId ? [clientId] : undefined, searchValue).then((res: any) => {
    const serviceInfos: any[] = [];
    res.content.map((serviceInfo: IServiceInfo) => {
      const exists = selected?.some((it) => it?.id === serviceInfo.id);
      if (!exists) {
        serviceInfos.push({
          id: serviceInfo.id,
          value: serviceInfo,
          label: serviceInfo.name ? serviceInfo.tag + ' - ' + serviceInfo.name : serviceInfo.tag,
          name: serviceInfo.tag
        });
      }
    });
    return serviceInfos;
  })
}

export async function getRoutesByServiceInfo(routeService: RouteService, serviceInfoId: number, searchValue?: string, specificPage?: number, selected?: any) {
  const routes: any[] = [];
  await routeService.getRoutesSearch([serviceInfoId], specificPage ? specificPage : routesPage, 10, searchValue).then((res: any) => {
    res.content.map((route: IRoute) => {
      const exists = selected?.id === route.id;
      if (!exists) {
        routes.push({
          id: route.id,
          value: route,
          label: route.name,
          name: route.name,
        });
      }
    });
  });
  return routes;
}

export async function getExpeditionsByRouteId(tripsService: TripsService, routeId: number, selected?: any[], page?: number, size?: number, search?: string) {
  return getExpeditionOptions(
    tripsService,
    { routes: [{ id: routeId } as IBase], page, size, search, dateOnly: true, excludedIds: selected?.map((item) => item.id) },
    tripSummaryOption,
  );
}